import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isAddress, getAddress } from 'viem';
import { useAccount } from 'wagmi';

const STORAGE_KEY = 'scrooge_ref';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const ReferralCodeContext = createContext({
  refCode: null,
  clearRefCode: () => {},
  setRefCodeManually: () => {},
});

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.addr || !parsed.ts) return null;
    if (Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return isAddress(parsed.addr) ? getAddress(parsed.addr) : null;
  } catch {
    return null;
  }
}

function writeStored(addr) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ addr: getAddress(addr), ts: Date.now() }),
    );
  } catch {
    /* storage full / private mode — silent */
  }
}

export function ReferralCodeProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { address } = useAccount();
  const [refCode, setRefCode] = useState(() => readStored());

  // 1. Парсим ?ref=0x… из URL и сохраняем (если валидный)
  useEffect(() => {
    const urlRef = searchParams.get('ref');
    if (!urlRef) return;
    if (!isAddress(urlRef)) {
      // мусор — чистим query param, не мешаем SEO
      searchParams.delete('ref');
      setSearchParams(searchParams, { replace: true });
      return;
    }
    const checksum = getAddress(urlRef);
    writeStored(checksum);
    setRefCode(checksum);
  }, [searchParams, setSearchParams]);

  // 2. Self-ref: если пользователь коннектит тот же адрес, который у него в ref — очищаем
  useEffect(() => {
    if (!address || !refCode) return;
    if (getAddress(address) === getAddress(refCode)) {
      localStorage.removeItem(STORAGE_KEY);
      setRefCode(null);
    }
  }, [address, refCode]);

  const value = useMemo(
    () => ({
      refCode,
      clearRefCode: () => {
        localStorage.removeItem(STORAGE_KEY);
        setRefCode(null);
      },
      setRefCodeManually: (addr) => {
        if (!isAddress(addr)) return;
        const checksum = getAddress(addr);
        writeStored(checksum);
        setRefCode(checksum);
      },
    }),
    [refCode],
  );

  return (
    <ReferralCodeContext.Provider value={value}>{children}</ReferralCodeContext.Provider>
  );
}

export function useReferralCode() {
  return useContext(ReferralCodeContext);
}
